import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect, MinimizedModalStrip } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from '../../../../services/notificationService';
import {
  taskApi, TaskListItemDto, CreateTaskRequest,
  clientApi, ClientDto,
  projectApi, ProjectDto,
  taskTypeApi, TaskTypeDto,
} from 'api/services/timeTrackerApi';
import TaskDetailScreen from './TaskDetailScreen';
import { formatHM } from './duration';

const blankCreate: CreateTaskRequest = { clientId: '', projectId: null, taskTypeId: '', title: '', description: '' };

// ── Recent tasks (per-browser, LRU, max 8) ─────────────────────────────────
type RecentTask = { id: string; title: string; clientName: string };
const RECENT_KEY = 'tt:recent-tasks';
const RECENT_MAX = 8;

const loadRecents = (): RecentTask[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch { return []; }
};

const pushRecent = (entry: RecentTask): RecentTask[] => {
  const current = loadRecents().filter(r => r.id !== entry.id);
  const next = [entry, ...current].slice(0, RECENT_MAX);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* quota — ignore */ }
  return next;
};

interface TaskListScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const TaskListScreen: React.FC<TaskListScreenProps> = ({ tabId, moduleName }) => {
  const { minimize, isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  /**
   * When non-null, the detail view opens in a modal on top of the list.
   * main_layout doesn't expose cross-module navigation to child screens,
   * so this screen owns the detail view as a modal rather than navigating away.
   */
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const detailCloseGuardRef = useRef<(() => void) | null>(null);

  const [rows, setRows] = useState<TaskListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterTaskTypeId, setFilterTaskTypeId] = useState('');
  const [filterStatus, setFilterStatus] = useState<'' | 'Open' | 'Closed'>('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskTypeDto[]>([]);

  // Create-task modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTaskRequest>(blankCreate);
  const [createSaving, setCreateSaving] = useState(false);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [createDiscardOpen, setCreateDiscardOpen] = useState(false);
  const createDirty = useMemo(
    () => JSON.stringify(createForm) !== JSON.stringify(blankCreate),
    [createForm]
  );
  const requestCreateClose = useCallback(() => {
    if (createSaving) return;
    if (createDirty) { setCreateDiscardOpen(true); return; }
    setCreateOpen(false);
  }, [createSaving, createDirty]);

  // Minimize plumbing for the Create Task modal
  const createFormRef = useRef(createForm); createFormRef.current = createForm;
  const isCreateRestoreRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!createOpen) return;
    if (isCreateRestoreRef.current) { isCreateRestoreRef.current = false; }
  }, [createOpen]);

  const handleMinimizeCreate = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentForm = { ...createFormRef.current };
    const titleStr = currentForm.title?.trim();
    const title = titleStr ? `Add - ${titleStr} (1)` : `Add New Task (1)`;
    minimize({
      id: `${tabId}-form-modal-create-task-1`,
      title,
      moduleName,
      tabId,
      isDirty: createDirty,
      onRestore: () => {
        isCreateRestoreRef.current = true;
        setCreateForm(currentForm);
        setCreateOpen(true);
      },
    });
    setCreateOpen(false);
  }, [tabId, moduleName, minimize, createDirty]);

  // Delete-task confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TaskListItemDto | null>(null);

  // Bulk close
  const [selectedTasks, setSelectedTasks] = useState<TaskListItemDto[]>([]);
  const [bulkCloseOpen, setBulkCloseOpen] = useState(false);
  const [bulkClosing, setBulkClosing]     = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, t] = await Promise.all([
          clientApi.list({ pageSize: 200 }),
          projectApi.list({ pageSize: 200 }),
          taskTypeApi.list({ pageSize: 200 }),
        ]);
        setClients(c.items);
        setProjects(p.items);
        setTaskTypes(t.items);
      } catch { /* lookup errors are non-fatal */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taskApi.list({
        page: page + 1,
        pageSize,
        searchTerm: searchQuery || undefined,
        clientId: filterClientId || undefined,
        projectId: filterProjectId || undefined,
        taskTypeId: filterTaskTypeId || undefined,
        status: filterStatus || undefined,
      });
      setRows(res.items);
      setTotalCount(res.totalCount);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, filterClientId, filterProjectId, filterTaskTypeId, filterStatus]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  // ── Recent tasks (per-browser) ───────────────────────────────────────
  const [recents, setRecents] = useState<RecentTask[]>(() => loadRecents());

  // ── In-page navigation between list ↔ detail ─────────────────────────
  const openTask = useCallback((id: string, meta?: { title: string; clientName: string }) => {
    setSelectedTaskId(id);
    if (meta) setRecents(pushRecent({ id, title: meta.title, clientName: meta.clientName }));
  }, []);
  const closeDetail = useCallback(() => {
    setSelectedTaskId(null);
    load();
  }, [load]);

  // ── Create task modal ────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    const modalId = tabId ? `${tabId}-form-modal-create-task-1` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setCreateForm(blankCreate);
    setCreateErrors({});
    setCreateOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const submitCreate = async () => {
    const errs: Record<string, string> = {};
    if (!createForm.clientId)      errs.clientId   = 'Client is required';
    if (!createForm.taskTypeId)    errs.taskTypeId = 'Type is required';
    if (!createForm.title.trim())  errs.title      = 'Title is required';
    setCreateErrors(errs);
    if (Object.keys(errs).length) return;

    setCreateSaving(true);
    try {
      const created = await taskApi.create({
        ...createForm,
        projectId: createForm.projectId || null,
      });
      notificationService.toast.success(`Task "${created.title}" created.`);
      setCreateOpen(false);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Create failed.');
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Grid ─────────────────────────────────────────────────────────────
  type GridRow = TaskListItemDto & { id: string };
  const columns: SyutGridColumn<GridRow>[] = useMemo(() => [
    { id: 'title', header: 'Title', accessorKey: 'title', size: 300,
      cell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); openTask(row.original.id, { title: row.original.title, clientName: row.original.clientName }); }}
          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, padding: 0, textAlign: 'left' }}
        >
          {row.original.title}
        </button>
      ),
    },
    { id: 'clientName',   header: 'Client',   accessorKey: 'clientName',   size: 200 },
    { id: 'projectName',  header: 'Project',  accessorKey: 'projectName',  size: 180,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'taskTypeName', header: 'Type',     accessorKey: 'taskTypeName', size: 150 },
    { id: 'status', header: 'Status', accessorKey: 'status', size: 110,
      cell: ({ getValue }) => {
        const s = getValue() as string;
        return (
          <span className={`badge ${s === 'Open' ? 'badge-active' : 'badge-inactive'}`}>
            {s}
          </span>
        );
      },
    },
    { id: 'createdUserName', header: 'Created by', accessorKey: 'createdUserName', size: 160,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'totalHours', header: 'Duration', accessorKey: 'totalHours', size: 120,
      cell: ({ getValue }) => formatHM(Number(getValue() ?? 0)),
    },
  ], [openTask]);

  const requestDelete = useCallback((row: TaskListItemDto) => {
    setPendingDelete(row);
    setDeleteOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await taskApi.remove(pendingDelete.id, pendingDelete.rowVersion);
      setDeleteOpen(false);
      setPendingDelete(null);
      await load();
      notificationService.toast.success(`Task "${pendingDelete.title}" deleted.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const rowActions = useCallback((row: GridRow): ActionMenuItem[] => {
    const hasEntries = Number(row.totalHours ?? 0) > 0;
    return [
      { id: 'open', label: 'Open / log time', icon: <IconSystem.eye size={16} />, onClick: () => openTask(row.id, { title: row.title, clientName: row.clientName }) },
      {
        id: 'delete',
        label: hasEntries ? 'Delete (has time entries)' : 'Delete',
        icon: <IconSystem.trash size={16} />,
        onClick: () => requestDelete(row),
        variant: 'danger',
        disabled: hasEntries,
      },
    ];
  }, [openTask, requestDelete]);

  const openTasksInSelection = useMemo(
    () => selectedTasks.filter(t => t.status === 'Open'),
    [selectedTasks]
  );

  const confirmBulkClose = async () => {
    if (openTasksInSelection.length === 0) return;
    setBulkClosing(true);
    try {
      const results = await Promise.allSettled(
        openTasksInSelection.map(t => taskApi.close(t.id, t.rowVersion))
      );
      const ok    = results.filter(r => r.status === 'fulfilled').length;
      const fail  = results.length - ok;
      setBulkCloseOpen(false);
      setSelectedTasks([]);
      await load();
      if (fail === 0) {
        notificationService.toast.success(`Closed ${ok} task${ok === 1 ? '' : 's'}.`);
      } else if (ok === 0) {
        notificationService.toast.error(`Failed to close ${fail} task${fail === 1 ? '' : 's'}.`);
      } else {
        notificationService.toast.warning(`Closed ${ok}, ${fail} failed.`);
      }
    } finally {
      setBulkClosing(false);
    }
  };

  const actions: ActionButton[] = useMemo(() => {
    const a: ActionButton[] = [];
    if (openTasksInSelection.length > 0) {
      a.push({
        id: 'bulk-close',
        icon: <IconSystem.lock size={18} />,
        label: `Close ${openTasksInSelection.length}`,
        onClick: () => setBulkCloseOpen(true),
        tooltip: `Close ${openTasksInSelection.length} selected task${openTasksInSelection.length === 1 ? '' : 's'}`,
        variant: 'danger',
      });
    }
    a.push(
      { id: 'add',     icon: <IconSystem.add     size={18} />, label: 'New Task', onClick: openCreate, tooltip: 'Create a new task', variant: 'icon' },
      { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',  onClick: load,       tooltip: 'Refresh',           variant: 'icon' },
    );
    return a;
  }, [openTasksInSelection.length, openCreate, load]);

  const anyFilterActive = !!(filterClientId || filterProjectId || filterTaskTypeId || filterStatus);
  const clientOptions  = useMemo(() => [{ value: '', label: 'All clients'  }, ...clients.map(c  => ({ value: c.id, label: c.name }))], [clients]);
  const projectOptions = useMemo(() => [{ value: '', label: 'All projects' }, ...projects.map(p => ({ value: p.id, label: p.name }))], [projects]);
  const typeOptions    = useMemo(() => [{ value: '', label: 'All types'    }, ...taskTypes.map(t => ({ value: t.id, label: t.name }))], [taskTypes]);

  const clearFilters = useCallback(() => {
    setFilterClientId(''); setFilterProjectId(''); setFilterTaskTypeId(''); setFilterStatus('');
    setPage(0);
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Tasks"
        description="Browse tasks, log time, close them out."
        icon={<IconSystem.list size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search tasks…' }}
        filterConfig={{ onClick: () => setFilterOpen(o => !o), isActive: filterOpen || anyFilterActive }}
        actions={actions}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {!searchQuery && !anyFilterActive && recents.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.75rem 0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)',
              flexShrink: 0,
            }}>
              <IconSystem.clock size={12} />
              <span style={{ fontWeight: 600, marginRight: '0.25rem' }}>Recent:</span>
              {recents.map(r => (
                <button
                  key={r.id}
                  onClick={() => openTask(r.id, { title: r.title, clientName: r.clientName })}
                  title={`${r.title} · ${r.clientName}`}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '999px',
                    padding: '0.15rem 0.625rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    color: 'var(--text-primary)',
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.title}
                </button>
              ))}
              <button
                onClick={() => { localStorage.removeItem(RECENT_KEY); setRecents([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.15rem 0.375rem', fontSize: '0.7rem' }}
                title="Clear recents"
              >
                Clear
              </button>
            </div>
          )}
          <div className="data-grid-container" style={{ flex: 1, minHeight: 0 }}>
            <SyutGrid
              data={rows as GridRow[]}
              columns={columns}
              enableRowSelection
              onSelectionChange={setSelectedTasks}
              enablePagination
              manualPagination
              totalCount={totalCount}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
              rowActions={rowActions}
              emptyMessage={loading ? 'Loading…' : 'No tasks found.'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', color: 'var(--text-muted)' }}>
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Client"
                  options={clientOptions}
                  value={filterClientId}
                  onChange={(val) => { setFilterClientId((val as string) || ''); setPage(0); }}
                  placeholder="All clients"
                  isClearable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Project"
                  options={projectOptions}
                  value={filterProjectId}
                  onChange={(val) => { setFilterProjectId((val as string) || ''); setPage(0); }}
                  placeholder="All projects"
                  isClearable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Type"
                  options={typeOptions}
                  value={filterTaskTypeId}
                  onChange={(val) => { setFilterTaskTypeId((val as string) || ''); setPage(0); }}
                  placeholder="All types"
                  isClearable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '',       label: 'All' },
                    { value: 'Open',   label: 'Open' },
                    { value: 'Closed', label: 'Closed' },
                  ]}
                  value={filterStatus}
                  onChange={(val) => { setFilterStatus(((val as string) || '') as any); setPage(0); }}
                  placeholder="All"
                  isClearable
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Done</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={bulkCloseOpen}
        title={`Close ${openTasksInSelection.length} task${openTasksInSelection.length === 1 ? '' : 's'}`}
        message={
          selectedTasks.length === openTasksInSelection.length
            ? `Close ${openTasksInSelection.length} selected task${openTasksInSelection.length === 1 ? '' : 's'}? You can reopen them later.`
            : `${openTasksInSelection.length} of ${selectedTasks.length} selected tasks are Open and will be closed. The rest are already closed and will be skipped.`
        }
        confirmLabel="Close tasks"
        cancelLabel="Cancel"
        variant="danger"
        loading={bulkClosing}
        loadingMessage="Closing…"
        onConfirm={confirmBulkClose}
        onCancel={() => setBulkCloseOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Task"
        message={`Delete "${pendingDelete?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteOpen(false); setPendingDelete(null); }}
      />

      <ConfirmDialog
        open={createDiscardOpen}
        title="Discard Changes?"
        message="You have unsaved changes to this task. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setCreateDiscardOpen(false); setCreateOpen(false); }}
        onCancel={() => setCreateDiscardOpen(false)}
      />

      {/* Task Detail Modal — log time / view entries without leaving the list */}
      {selectedTaskId && (
        <div className="modal-backdrop" onClick={() => detailCloseGuardRef.current?.()}>
          <div
            className="modal-container"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 1100, width: '95%', height: '90vh', maxHeight: '90vh' }}
          >
            <TaskDetailScreen
              id={selectedTaskId}
              onBack={closeDetail}
              modalMode
              closeGuardRef={detailCloseGuardRef}
            />
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {createOpen && (
        <div className="modal-backdrop" onClick={requestCreateClose}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.add size={16} style={{ marginRight: 8 }} />
                New Task
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {tabId && moduleName && (
                  <button className="modal-header-btn" onClick={handleMinimizeCreate} title="Minimize to bar" disabled={createSaving}>
                    <IconSystem.chevronDown size={16} />
                  </button>
                )}
                <button className="modal-close" onClick={requestCreateClose}>
                  <IconSystem.x size={18} />
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Client<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <select
                    className={`form-select ${createErrors.clientId ? 'form-field-error' : ''}`}
                    value={createForm.clientId}
                    onChange={(e) => setCreateForm({ ...createForm, clientId: e.target.value })}
                    autoFocus
                  >
                    <option value="">— Choose a client —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {createErrors.clientId && <div className="form-error-message">{createErrors.clientId}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Type<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <select
                    className={`form-select ${createErrors.taskTypeId ? 'form-field-error' : ''}`}
                    value={createForm.taskTypeId}
                    onChange={(e) => setCreateForm({ ...createForm, taskTypeId: e.target.value })}
                  >
                    <option value="">— Choose a type —</option>
                    {taskTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  {createErrors.taskTypeId && <div className="form-error-message">{createErrors.taskTypeId}</div>}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Project</label>
                <select
                  className="form-select"
                  value={createForm.projectId ?? ''}
                  onChange={(e) => setCreateForm({ ...createForm, projectId: e.target.value || null })}
                >
                  <option value="">— None —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Title<span style={{ color: 'var(--danger)' }}> *</span></label>
                <input
                  className={`form-input ${createErrors.title ? 'form-field-error' : ''}`}
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  maxLength={300}
                />
                {createErrors.title && <div className="form-error-message">{createErrors.title}</div>}
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  rows={4}
                  value={createForm.description ?? ''}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={requestCreateClose} disabled={createSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={submitCreate} disabled={createSaving}>
                {createSaving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskListScreen;
